import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Wraps a page/section behind an admin-configured password lock.
 *
 * <PageLock pageKey="settings" label="Settings">
 *   <SettingsPage />
 * </PageLock>
 *
 * - Fetches /api/settings/page-locks to learn if this key is enabled.
 * - If enabled, looks for an unlock token in sessionStorage and validates it.
 * - Otherwise shows a password modal.
 * - On success, stores `cts_pl_<pageKey>` in sessionStorage (24h server-side TTL).
 */
export default function PageLock({ pageKey, label = 'this section', children }) {
  const [status, setStatus] = useState('loading'); // loading | unlocked | locked
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validate = useCallback(async () => {
    try {
      const locks = await api.get('/settings/page-locks').then(r => r.data || {});
      const cfg = locks[pageKey];
      if (!cfg || !cfg.enabled || !cfg.has_password) {
        setStatus('unlocked');
        return;
      }
      // Always prompt — unlock is per-visit, not cached.
      setStatus('locked');
    } catch {
      // Fail-open if the lock service is unreachable — don't brick the page for admins
      setStatus('unlocked');
    }
  }, [pageKey]);

  useEffect(() => { validate(); }, [validate]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!password) return;
    setSubmitting(true);
    try {
      await api.post(`/settings/page-locks/${pageKey}/verify`, { password });
      setStatus('unlocked');
      setPassword('');
      toast.success('Unlocked');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Incorrect password';
      toast.error(msg);
    }
    setSubmitting(false);
  };

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400 text-sm" data-testid={`pagelock-loading-${pageKey}`}>
        <Lock className="w-4 h-4 mr-2 animate-pulse" /> Checking access…
      </div>
    );
  }

  if (status === 'locked') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid={`pagelock-prompt-${pageKey}`}>
        <form onSubmit={handleSubmit} className="w-full max-w-sm bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-5 h-5 text-[#E31937]" />
            <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">Locked</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">Enter the password to access {label}.</p>
          <Input
            type="password"
            autoFocus
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            className="bg-gray-50"
            data-testid={`pagelock-input-${pageKey}`}
          />
          <Button type="submit" disabled={submitting || !password} className="w-full mt-4 bg-[#E31937] hover:bg-[#C4122C] text-white" data-testid={`pagelock-submit-${pageKey}`}>
            <Lock className="w-3 h-3 mr-1" /> {submitting ? 'Unlocking…' : 'Unlock'}
          </Button>
          <p className="text-[10px] text-gray-400 mt-3 text-center">
            Re-prompts every visit. Set/change the password in <strong>Settings → Roles &amp; Menu</strong>.
          </p>
        </form>
      </div>
    );
  }

  return children;
}
